// ============================================================================
// SERVICE (master) SERVICE — entity-scoped via X-Entity-ID. Includes BU descriptions.
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { serviceEndpoints } from '../endpoints/commercial.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, BackendPagedResult, PaginatedResponse, EmptyResponse, ID,
  ServiceDto, CreateServiceRequest, UpdateServiceRequest, ServiceQueryParams,
} from '../types';

class ServiceService {
  async getAll(params?: ServiceQueryParams): Promise<PaginatedResponse<ServiceDto>> {
    const endpoint = params ? buildEndpointWithQuery(serviceEndpoints.list(), params) : serviceEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<ServiceDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
        totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  async getById(id: ID): Promise<ServiceDto> {
    const response = await get<ApiResponse<ServiceDto>>(serviceEndpoints.getById(id));
    return response.data.data;
  }

  async create(data: CreateServiceRequest): Promise<ServiceDto> {
    const response = await post<ApiResponse<ServiceDto>>(serviceEndpoints.create(), data);
    return response.data.data;
  }

  async update(id: ID, data: UpdateServiceRequest): Promise<ServiceDto> {
    const response = await put<ApiResponse<ServiceDto>>(serviceEndpoints.update(id), data);
    return response.data.data;
  }

  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const url = buildEndpointWithQuery(serviceEndpoints.delete(id), { rowVersion });
    const response = await del<EmptyResponse>(url);
    return response.data;
  }

  /** The code the next service (itemType REIMBURSEMENT for a reimbursable item) would receive when none is typed. */
  async getNextCode(itemType?: string): Promise<string> {
    const endpoint = serviceEndpoints.nextCode();
    const url = itemType ? buildEndpointWithQuery(endpoint, { itemType }) : endpoint;
    const response = await get<ApiResponse<string>>(url);
    return response.data.data;
  }

  async isCodeAvailable(code: string, excludeId?: ID): Promise<boolean> {
    const endpoint = serviceEndpoints.validateCode(code);
    const url = excludeId ? buildEndpointWithQuery(endpoint, { excludeId }) : endpoint;
    const response = await get<ApiResponse<boolean>>(url);
    return response.data.data;
  }
}

export const serviceService = new ServiceService();
export default serviceService;
